import * as React from "react";
import { useState } from "react";
import {
  DataGrid,
  GridFooterContainer,
  useGridApiContext,
  useGridSelector,
  gridRowCountSelector,
  gridPageSelector,
  gridPageCountSelector,
} from "@mui/x-data-grid";
import { Pagination } from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Checkbox,
} from "@mui/material";
import { API_DOMAIN_URL } from "../Common";

const PAGE_SIZE = 10;

function CustomFooter() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);

  const from = rowCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, rowCount);
  const totalPages = Math.max(pageCount, 1);

  return (
    <GridFooterContainer>
      <div style={{ padding: "0 16px", fontSize: "13px" }}>
        {`${from}-${to} / ${rowCount}件`}
      </div>
      <Pagination
        count={totalPages}
        page={page + 1}
        onChange={(_, value) => apiRef.current.setPage(value - 1)}
        size="small"
      />
    </GridFooterContainer>
  );
}

export default function ReportedUserListDataTable({
  orgUser,
  reportedUserList,
}) {
  const navigater = useNavigate();
  const [selectedYearMonths, setSelectedYearMonths] = useState([]);
  const [measurementYearMonth, setMeasurementYearMonth] = useState("");

  const clicked2 = (element) => {
    if (element != undefined && element.row != undefined && orgUser) {
      navigater(`/HistoryDetail/${element.row.YearMonth}/${orgUser.id}`);
    }
  };

  const handleCheckboxChange = (yearMonth, checked) => {
    if (checked) {
      setSelectedYearMonths([...selectedYearMonths, yearMonth]);
    } else {
      setSelectedYearMonths(
        selectedYearMonths.filter((ym) => ym !== yearMonth)
      );
    }
  };

  const generateMonthOptions = () => {
    const options = [];
    for (let i = 0; i >= -17; i--) {
      const dt = new Date();
      dt.setDate(1);
      dt.setMonth(dt.getMonth() + i);
      const year = dt.getFullYear().toString();
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      options.push({
        val: `${year}${month}`,
        name: `${year}/${dt.getMonth() + 1}`,
      });
    }
    return options;
  };

  const handleCsvDownload = async () => {
    const sorted = [...selectedYearMonths].sort();
    const minYM = sorted[0];
    const maxYM = sorted[sorted.length - 1];

    const from = `${minYM.slice(0, 4)}/${minYM.slice(4, 6)}/01 00:00:00`;
    const toYear = parseInt(maxYM.slice(0, 4));
    const toMonth = parseInt(maxYM.slice(4, 6));
    const lastDay = new Date(toYear, toMonth, 0).getDate();
    const lastDayStr = String(lastDay).padStart(2, "0");
    const toYearStr = maxYM.slice(0, 4);
    const toMonthStr = maxYM.slice(4, 6);
    const to = `${toYearStr}/${toMonthStr}/${lastDayStr} 23:59:59`;

    const menuId =
      orgUser.MenuListId && orgUser.MenuListId !== ""
        ? orgUser.MenuListId
        : "none";

    const code = process.env.REACT_APP_API_CODE;
    const url =
      API_DOMAIN_URL +
      `/api/CreateReportCSV?code=${code}&UserId=${orgUser.id}&From=${from}&To=${to}&Period=${measurementYearMonth}&MenuId=${menuId}`;

    const response = await fetch(url);
    const csvText = await response.text();

    // BOM付きUTF-8でExcelが文字化けしないようにする
    const blob = new Blob(["\uFEFF" + csvText], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `測定年月${measurementYearMonth}_対象範囲${minYM}01_${toYearStr}${toMonthStr}${lastDayStr}.csv`;
    link.click();
  };

  const columns = [
    {
      field: "YearMonth",
      headerName: "年月",
      width: 200,
      valueFormatter: (params) =>
        `${params.value.slice(0, 4)}年${params.value.slice(4, 6)}月`,
    },
    {
      field: "HowManyUser",
      headerName: "人数",
      width: 150,
      valueGetter: (params) => `${params.row.HowManyUser}名`,
    },
    {
      field: "csvSelect",
      headerName: "CSV出力",
      sortable: false,
      width: 100,
      renderCell: (params) => (
        <Checkbox
          checked={selectedYearMonths.includes(params.row.YearMonth)}
          onChange={(e) =>
            handleCheckboxChange(params.row.YearMonth, e.target.checked)
          }
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
  ];

  const isDisabled = selectedYearMonths.length === 0 || !measurementYearMonth;

  return (
    <div style={{ width: "100%" }}>
      <Typography variant="h6">評価シートご利用状況</Typography>
      <div style={{ width: "100%" }}>
        <DataGrid
          rows={
            reportedUserList
              ? [...reportedUserList].sort((a, b) =>
                  b.YearMonth > a.YearMonth ? 1 : -1
                )
              : []
          }
          columns={columns}
          pageSize={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          checkboxSelection={false}
          density="compact"
          autoHeight
          onRowClick={(ele2) => clicked2(ele2)}
          components={{ Footer: CustomFooter }}
        />
      </div>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>測定年月</InputLabel>
          <Select
            value={measurementYearMonth}
            label="測定年月"
            onChange={(e) => setMeasurementYearMonth(e.target.value)}
          >
            {generateMonthOptions().map((opt) => (
              <MenuItem key={opt.val} value={opt.val}>
                {opt.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          disabled={isDisabled}
          onClick={handleCsvDownload}
        >
          CSV出力
        </Button>
        <Typography variant="body2" sx={{ ml: 2 }}>
          ※評価シート出力を行ったデータを対象にCSVファイルで保存できます。
          <br />
          ※CSV出力を行っても費用は加算されません。
        </Typography>
      </Box>
      <Box sx={{ mb: 4 }} />
    </div>
  );
}

ReportedUserListDataTable.propTypes = {
  orgUser: PropTypes.object,
  reportedUserList: PropTypes.object,
};
